import { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"

import { defaultAxiosInstance } from "./client"
import { ResponseData } from "./common"

const defaultUrl = `${process.env.REACT_APP_BASE_API_URL || ""}/PMS/token`

export interface PmsSystemFieldsResponseData extends ResponseData {
  data: string[]
}

export interface PmsSystemFieldsResponse extends AxiosResponse {
  data: PmsSystemFieldsResponseData
}

export interface PmsSystemFieldsRequestData {
  pmsSystem: string
}

export interface PmsSystemFieldsRequest extends Partial<AxiosRequestConfig> {
  params: PmsSystemFieldsRequestData
}

export function pmsSystemFields(
  {
    params,
    method = "get",
    url = `${defaultUrl}/format`,
    ...config
  }: PmsSystemFieldsRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<PmsSystemFieldsResponse> {
  return axiosInstance({
    params,
    method,
    url,
    ...config,
  }) as Promise<PmsSystemFieldsResponse>
}

export interface FeeInfoType {
  applied: string
  description: string
  effectiveOn: string
  effectiveTo: string
  taxable: boolean
  type: string
  value: number
  taxRate: number
  minStayLength?: number | null
  maxStayLength?: number | null
}

export interface TaxInfoType {
  description: string
  percent: number
  taxType: string
  type: string
  effectiveOn: string
  effectiveTo: string
}

export interface SaveTokenData {
  data: {
    message: string | null
    rateType: { id: string; name: string }[]
    status: boolean
  } | null
  fields: string[] | null // ["id", "name"],
  message: string | null
  pmcId: string
  saved: boolean
}

export interface SaveTokenResponseData extends ResponseData {
  data: SaveTokenData
}

export interface SaveTokenResponse extends AxiosResponse {
  data: SaveTokenResponseData
}

export type SelectedRate = {
  whimstayRateName: string
  trackRateName: string
  rateId: string
}

export interface SaveTokenRequest extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
  }
  data: {
    clientId?: string
    clientSecret?: string
    companyName?: string
    endPoint?: string
    pmcId?: string
    pmsNameForToken?: string
    rateTypeId?: number | string
    token?: string
    tokenType?: string
    apiKey?: string
    rateTypes?: SelectedRate[]
    rateInfo?: {
      feeInfo: FeeInfoType[]
      taxInfo: TaxInfoType[]
    }
  }
}

export function saveTokenApi(
  { params, data, ...config }: SaveTokenRequest,
  axiosInstance: AxiosInstance,
): Promise<SaveTokenResponse> {
  return axiosInstance({
    method: "post",
    params,
    data,
    url: `${defaultUrl}/save`,
    ...config,
  }) as Promise<SaveTokenResponse>
}

export interface SavePMCConfigApiData {
  data: {
    message: string | null
    rateType: { id: string; name: string }[]
    status: boolean
  } | null
  fields: string[] | null // ["id", "name"],
  message: string | null
  pmcId: string
  saved: boolean
}

export interface SavePMCConfigApiResponseData extends ResponseData {
  data: SavePMCConfigApiData
}

export interface SavePMCConfigApiResponse extends AxiosResponse {
  data: SavePMCConfigApiResponseData
}

export interface SavePMCConfigApiRequest extends Partial<AxiosRequestConfig> {
  params: {
    pmcId: string
  }
  data: {
    cancellationPolicy: string
    pdp: string
    discountPr: number
    newListingDiscountPr: number
    isAutoPublish: boolean
    isAgreeForLastMinDiscount: boolean
    contactFullName: string
    contactEmail: string
    contactPhoneNumber: string
    contactPhoneNumberDialerCode: string
  }
}

export function savePMCConfigApi(
  { params, data, ...config }: SavePMCConfigApiRequest,
  axiosInstance: AxiosInstance,
): Promise<SavePMCConfigApiResponse> {
  return axiosInstance({
    method: "post",
    params,
    data,
    url: `${defaultUrl}/save/pmc-config`,
    ...config,
  }) as Promise<SavePMCConfigApiResponse>
}

export interface TrackRateTypeApiRequestData {
  clientId: string
  clientSecret: string
  endPoint: string
}

export interface TrackRateTypeApiRequest extends Partial<AxiosRequestConfig> {
  params: TrackRateTypeApiRequestData
}

export interface TrackRateTypeApiData {
  message: string | null
  rateType: { id: string; name: string }[]
  status: boolean
}

export interface TrackRateTypeApiResponseData extends ResponseData {
  data: TrackRateTypeApiData | null
}

export interface TrackRateTypeApiResponse extends AxiosResponse {
  data: TrackRateTypeApiResponseData
}

export function trackRateTypeApi(
  {
    params,
    method = "post",
    url = `${defaultUrl}/track/rate-type`,
    ...config
  }: TrackRateTypeApiRequest,
  axiosInstance: AxiosInstance = defaultAxiosInstance,
): Promise<TrackRateTypeApiResponse> {
  return axiosInstance({
    data: params,
    method,
    url,
    ...config,
  }) as Promise<TrackRateTypeApiResponse>
}
